import { createErrorHandler } from '../boundaries/error';
import { pageHandler } from '../boundaries/page';
import { goToHome, openFlightSummary } from '../boundaries/flow';
import { createRouteMatcher, createFeaturesQueryRequest, aliases as reactHooksAliases, createAppProviderCoreProps } from '@edreamsodigeo/frontend-core';
import { createTrackingHandler } from '../boundaries/tracking';

// Migrated
import { aliases as manageMyBookingAliases } from '@edreamsodigeo/frontend-manage-my-booking/aliases';
import { aliases as flexibilityBundleAliases } from '@edreamsodigeo/frontend-ancillary-bundle/aliases';
import { aliases as additionsAliases } from '@edreamsodigeo/frontend-additions/aliases';
import { aliases as supportPackAliases } from '@edreamsodigeo/frontend-support-pack/aliases';
import { aliases as guaranteesAliases } from '@edreamsodigeo/frontend-guarantees/aliases';
import { aliases as checkinAliases } from '@edreamsodigeo/frontend-checkin-components/aliases';
import { aliases as feBookingAdminAliases } from '@edreamsodigeo/frontend-booking-admin/aliases';
import { aliases as priceFreezeAliases } from '@edreamsodigeo/frontend-price-freeze/aliases';

// IMPORTANT: Please, ensure your import alias is added in front_test_utils/setup.js in frontend-html5
// If the alias is not included in html5 tests, CI will fail
// https://bitbucket.org/odigeoteam/frontend-html5/pull-requests/5439
import featuresFeAccommodation from '@edreamsodigeo/frontend-accommodation/features.json';
import featuresXSelling from '@edreamsodigeo/frontend-xselling-components/features.json';
import checkoutFeatures from '@edreamsodigeo/frontend-checkout/features.json';
import checkinFeatures from '@edreamsodigeo/frontend-checkin-components/features.json';
import flightResultsFeatures from '@edreamsodigeo/frontend-flights-results/features.json';
import membershipRetentionFeatures from '@edreamsodigeo/frontend-membership-retention/features.json';
import featuresFeUserAuth from '@edreamsodigeo/frontend-user-authentication/features.json';
import shoppingBasketFeatures from '@edreamsodigeo/frontend-shopping-basket/features.json';
import featuresHomePage from '@edreamsodigeo/frontend-homepage/features.json';
import featuresMembershipStandalone from '@edreamsodigeo/frontend-membership-standalone/features.json';
import featuresPrime from '@edreamsodigeo/frontend-prime/features.json';
import featuresPrimeSubscriptionReferralFlow from '@edreamsodigeo/frontend-prime-subscription-referral-flow/features.json';
import userAreaFeatures from '@edreamsodigeo/frontend-user-area/features.json';
import { createFrontendAPIClientProps } from '../boundaries/feapiClient';
import { getAppContext } from '../boundaries/tracking/trackTime';
import renewalPaymentMethodsFeatures from '@edreamsodigeo/frontend-renewal-payment-methods/features.json';
import insurancesFeatures from '@edreamsodigeo/frontend-insurances/features.json';
import prebookingAttractFeatures from '@edreamsodigeo/frontend-prebooking-attract/features.json';
import vehicleRentalFeatures from '@edreamsodigeo/frontend-vehicle-rental/features.json';
import seatsFeatures from '@edreamsodigeo/frontend-seats/features.json';
import cabinBagsFeatures from '@edreamsodigeo/frontend-cabin-bags/features.json';
import additionsFeatures from '@edreamsodigeo/frontend-additions/features.json';
import supportPackFeatures from '@edreamsodigeo/frontend-support-pack/features.json';
import funnelPathfinder from '@edreamsodigeo/frontend-funnel-pathfinder/features.json';
import priceFreezeFeatures from '@edreamsodigeo/frontend-price-freeze/features.json';
import ancillaryBundleFeatures from '@edreamsodigeo/frontend-ancillary-bundle/features.json';
import featuresPrimeBookingCancellation from '@edreamsodigeo/frontend-prime-booking-cancellation/features.json';
import inDestinationActivitiesFeatures from '@edreamsodigeo/frontend-in-destination-activities/features.json';
import fastTrackFeatures from '@edreamsodigeo/frontend-fast-track/features.json';
import marketingCampaignsFeatures from '@edreamsodigeo/frontend-marketing-campaigns/features.json';
const i18nextOptions = {
  parseMissingKeyHandler: () => ''
};
export const featureVariationsInput = createFeaturesQueryRequest([checkoutFeatures, checkinFeatures, flightResultsFeatures, featuresXSelling, featuresFeAccommodation, membershipRetentionFeatures, featuresFeUserAuth, shoppingBasketFeatures, featuresHomePage, featuresMembershipStandalone, featuresPrime, featuresPrimeSubscriptionReferralFlow, userAreaFeatures, renewalPaymentMethodsFeatures, insurancesFeatures, prebookingAttractFeatures, vehicleRentalFeatures, seatsFeatures, cabinBagsFeatures, additionsFeatures, supportPackFeatures, funnelPathfinder, priceFreezeFeatures, ancillaryBundleFeatures, featuresPrimeBookingCancellation, inDestinationActivitiesFeatures, fastTrackFeatures, marketingCampaignsFeatures], [reactHooksAliases, checkinAliases, priceFreezeAliases, feBookingAdminAliases, manageMyBookingAliases, flexibilityBundleAliases, additionsAliases, supportPackAliases, guaranteesAliases]);

/**
 * @param {string} route
 * @param {object} options
 * @param {boolean} options.trigger
 * @param {boolean} options.replace
 */

const urlRouteMap = {
  '': 'homeflights',
  '/': 'homeflights'
};
const parseNextPathnameToOdigeoRoute = route => {
  return urlRouteMap[route] || route;
};
const odigeoNavigate = (route, options) => {
  window.Odigeo.Router.navigate(parseNextPathnameToOdigeoRoute(route), options);
};
export const createAppProviderProps = async (_siteConfig, {
  routes,
  disableTracking,
  ...optionsProps
}) => {
  const routeMatcher = createRouteMatcher(routes);
  const SessionScope = window?.Odigeo?.SessionScope;
  const flow = window?.Odigeo?.SessionScope?.flow;
  const isNativeWebView = SessionScope?.interface?.startsWith('NATIVE_');
  const {
    brandCode,
    locale
  } = _siteConfig;
  const {
    trackSurvey,
    trackPage
  } = createTrackingHandler(window?.Odigeo);
  const googleClientId = window?.Odigeo?.SessionScope?.social?.googleLoginClientId;
  const facebookId = window.Odigeo?.SessionScope?.social?.facebookAppId;
  const marketingPortal = window.Odigeo?.SessionScope?.mktPortal;
  const errorHandler = createErrorHandler(window.Odigeo);
  const frontendAPIClientProps = createFrontendAPIClientProps({
    logger: SessionScope.logger,
    tokenFallback: SessionScope.visitInformation
  });
  const {
    queryClient
  } = frontendAPIClientProps;
  const appProviderProps = {
    ...optionsProps,
    routingProps: {
      routeMatcher,
      pageHandler,
      odigeoNavigate: odigeoNavigate
    },
    baseProps: {
      errorHandler: errorHandler,
      logger: SessionScope.logger,
      i18nextOptions: i18nextOptions,
      clientInterface: SessionScope.interface,
      currenyFormat: locale.currenyFormat,
      flow,
      flowActions: {
        goToHome,
        openFlightSummary
      },
      featureVariationsInput,
      context: getAppContext(),
      facebookId,
      googleClientId,
      marketingPortal
    },
    appErrorProps: {
      invalidateQueries: () => queryClient.invalidateQueries(),
      logService: '/frontend-api',
      frontVersion: SessionScope.oneFrontVersion
    },
    frontendAPIClientProps
  };
  if (disableTracking) {
    appProviderProps.baseProps = {
      ...appProviderProps.baseProps,
      storageClient: {}
    };
  } else {
    const {
      storageClient,
      customDimensionsRepository,
      pageTracker,
      datalayerTrackingClient,
      eCommerceTrackingClient,
      trackingParamsRepository,
      dataMeshTrackingClient,
      thirdPartyTrackingAgent
    } = await createAppProviderCoreProps({
      brandCode,
      locale,
      isNativeWebView,
      trackingAgent: {
        trackPage,
        trackSurvey,
        routeMatcher,
        routePageMap: {}
      }
    });
    appProviderProps.baseProps = {
      ...appProviderProps.baseProps,
      storageClient,
      pageTracker,
      customDimensionsRepository,
      datalayerTrackingClient,
      eCommerceTrackingClient,
      trackingParamsRepository,
      dataMeshTrackingClient,
      thirdPartyTrackingAgent
    };
  }
  return appProviderProps;
};